import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { C, radius, card, shadow } from '../styles';
import { useJobs } from '../context/JobsContext';

const API = 'https://reportautomation.finkraftai.com/api';

const ERROR_INFO = {
  FLAG_Duplicate_Invoice_Number:     ['Duplicate Invoice Number',       'High'],
  FLAG_Duplicate_Unique_ID:          ['Duplicate Unique Transaction ID','High'],
  FLAG_Customer_GSTIN_Mismatch:      ['Customer GSTIN Mismatch',        'High'],
  FLAG_Supplier_GSTIN_Mismatch:      ['Supplier GSTIN Mismatch',        'High'],
  FLAG_GST_Variance_GT_10:           ['GST Variance > ±10',             'High'],
  FLAG_CGST_NE_SGST:                 ['CGST ≠ SGST',                    'Medium'],
  FLAG_Incorrect_GST_Combo:          ['Incorrect GST Combination',      'High'],
  FLAG_Incorrect_GST_Head:           ['Incorrect GST Head',             'High'],
  FLAG_Customer_GSTIN_Length:        ['Customer GSTIN Length ≠ 15',     'Medium'],
  FLAG_Supplier_GSTIN_Length:        ['Supplier GSTIN Length ≠ 15',     'Medium'],
  FLAG_Invoice_Number_Length:        ['Invoice Number > 16 chars',      'Low'],
  FLAG_Future_Invoice_Date:          ['Future Invoice Date',             'High'],
  FLAG_Amount_Sign_Issue:            ['Amount Sign Issue',               'High'],
  FLAG_Invoice_Number_NULL:          ['Invoice Number NULL',             'High'],
  FLAG_Invoice_Date_NULL:            ['Invoice Date NULL',               'High'],
  FLAG_GST_GT_Taxable:               ['GST > Taxable Value',             'High'],
  FLAG_Same_Customer_Supplier_GSTIN: ['Same Customer & Supplier GSTIN',  'High'],
  FLAG_GSTIN_Mismatch_Inv_vs_2AB:    ['GSTIN Mismatch Invoice vs 2A/2B', 'High'],
  FLAG_Taxable_GT_Total:             ['Taxable Value > Total Amount',    'High'],
  FLAG_Vendor_NULL:                  ['Vendor NULL',                     'Medium'],
  FLAG_Invoice_Ends_0000:            ['Invoice Ends with 0000',          'Low'],
  FLAG_Invalid_Chars_Invoice:        ['Invalid Characters in Invoice',   'Low'],
  FLAG_CreditDebit_Date_LT_Original: ['Credit/Debit Date < Original',    'Medium'],
  FLAG_Customer_GSTIN_NULL:          ['Customer GSTIN NULL',             'Medium'],
  FLAG_Booking_Agency_Invoice_NULL:  ['Booking: Agency Invoice NULL',    'Medium'],
  FLAG_Booking_PNR_NULL:             ['Booking: PNR NULL',               'Medium'],
  FLAG_Duplicate_Composite_Key:      ['Duplicate Composite Key',         'High'],
  FLAG_Non_Kotak_Customer:           ['Non-Kotak Customer',              'Medium'],
  FLAG_Pending_Domestic:             ['Pending Domestic',                'Medium'],
  FLAG_Rate18_Transport_Check:       ['Rate=18 Transport Check',         'Medium'],
};

const SEV_STYLE = {
  High:   { background:'#FEE2E2', color:'#991B1B', border:'#FCA5A5' },
  Medium: { background:'#FEF3C7', color:'#92400E', border:'#FCD34D' },
  Low:    { background:'#ECFDF5', color:'#065F46', border:'#6EE7B7' },
};

function StatusBadge({ status }) {
  const map = {
    completed:  { bg: C.successBg, color: C.success, border: C.success },
    failed:     { bg: C.dangerBg,  color: C.danger,  border: C.danger  },
    processing: { bg: C.accentBg,  color: C.accent,  border: C.accent  },
  };
  const s = map[status] || map.processing;
  return (
    <span style={{
      background: s.bg, color: s.color, border:`1px solid ${s.border}44`,
      borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:600,
      display:'inline-flex', alignItems:'center', gap:5, textTransform:'capitalize',
    }}>
      {status === 'processing' && (
        <span style={{ width:6, height:6, borderRadius:'50%', background: C.accent,
                       animation:'pulse 1.2s infinite', flexShrink:0 }}/>
      )}
      {status}
    </span>
  );
}

function DetailModal({ run, onClose }) {
  if (!run) return null;
  const flags = Object.entries(run.flag_counts || {}).filter(([,c]) => c > 0)
                      .sort((a,b) => b[1]-a[1]);
  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, background:'rgba(15,23,42,.45)',
      backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:200,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, border:`1px solid ${C.border2}`,
        borderRadius: radius.xl, padding:'28px 32px', width:'min(720px,95vw)',
        maxHeight:'85vh', overflowY:'auto', animation:'slideUp .2s ease',
        boxShadow: shadow.lg,
      }}>
        <div style={{ display:'flex', alignItems:'center', marginBottom:20 }}>
          <div>
            <h2 style={{ fontFamily:'Syne,sans-serif', fontSize:18, fontWeight:600, color: C.text }}>
              Run Detail — {run.report_type ? run.report_type.toUpperCase() + " — " : ""}{run.filename}
            </h2>
            <p style={{ fontSize:11, color: C.muted, marginTop:4, fontFamily:'DM Mono,monospace' }}>
              ID: {run.run_id} &nbsp;·&nbsp; {run.started_at?.slice(0,16).replace('T',' ')}
              &nbsp;·&nbsp; {run.duration}s
            </p>
          </div>
          <button onClick={onClose} style={{
            marginLeft:'auto', background: C.surface2, border:`1px solid ${C.border2}`,
            borderRadius: radius.sm, width:32, height:32,
            color: C.muted, fontSize:18, cursor:'pointer', lineHeight:1,
          }}>×</button>
        </div>

        {/* stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
          {[
            ['Total',   run.original_count, C.accent],
            ['Clean',   run.clean_count,    C.success],
            ['Flagged', run.flagged_count,  C.danger],
            ['Removed', run.removed_count,  C.warn],
          ].map(([lbl, val, color]) => (
            <div key={lbl} style={{ background: C.surface2,
                                    border:`1px solid ${C.border}`,
                                    borderRadius:8, padding:'12px 14px', textAlign:'center' }}>
              <div style={{ fontSize:10, color: C.muted, marginBottom:4,
                            textTransform:'uppercase', letterSpacing:'.05em', fontWeight:600 }}>{lbl}</div>
              <div style={{ fontFamily:'DM Mono,monospace', fontSize:22,
                            fontWeight:600, color }}>{(val||0).toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>

        {/* flag breakdown */}
        {flags.length > 0 && (
          <>
            <div style={{ fontSize:12, fontWeight:700, color: C.muted,
                          textTransform:'uppercase', letterSpacing:'.06em', marginBottom:10 }}>
              Error flag breakdown
            </div>
            <div style={{ borderRadius: radius.md, overflow:'hidden',
                          border:`1px solid ${C.border2}`, marginBottom:20 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background: C.surface2 }}>
                    {['Error type','Count','% of data','Severity'].map(h => (
                      <th key={h} style={{ padding:'10px 14px', textAlign:'left',
                                           fontSize:11, color: C.muted, fontWeight:600,
                                           borderBottom:`1px solid ${C.border}`,
                                           textTransform:'uppercase', letterSpacing:'.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {flags.map(([flag, count]) => {
                    const [name, sev] = ERROR_INFO[flag] || [flag.replace('FLAG_',''), 'Medium'];
                    const sevS = SEV_STYLE[sev] || SEV_STYLE.Medium;
                    const pct = run.original_count ? (100*count/run.original_count).toFixed(1) : '0.0';
                    return (
                      <tr key={flag} style={{ borderBottom:`1px solid ${C.border}` }}>
                        <td style={{ padding:'9px 14px', fontSize:13, color: C.text }}>{name}</td>
                        <td style={{ padding:'9px 14px', fontFamily:'DM Mono,monospace',
                                     fontSize:13, color: C.text, fontWeight:500 }}>{count.toLocaleString('en-IN')}</td>
                        <td style={{ padding:'9px 14px', fontSize:12, color: C.muted }}>{pct}%</td>
                        <td style={{ padding:'9px 14px' }}>
                          <span style={{
                            background: sevS.background, color: sevS.color,
                            border:`1px solid ${sevS.border}`,
                            padding:'2px 9px', borderRadius:4,
                            fontSize:11, fontWeight:600 }}>{sev}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* download */}
        {run.output_file && (
          <a href={`${API}/download/${run.output_file}`} download style={{ textDecoration:'none' }}>
            <button style={{
              background:`linear-gradient(135deg, ${C.accent}, ${C.teal})`,
              border:'none', borderRadius: radius.md,
              padding:'11px 24px', color:'#fff', fontFamily:'Inter,sans-serif',
              fontSize:14, fontWeight:600, cursor:'pointer',
              boxShadow:'0 3px 10px rgba(8,145,178,0.25)',
            }}>⬇ Download Excel Report</button>
          </a>
        )}
      </div>
    </div>
  );
}

export default function HistoryPage() {
  const { jobList } = useJobs();
  const [serverHistory, setServerHistory] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selected,      setSelected]      = useState(null);
  const [filterClient,  setFilterClient]  = useState('All');
  const [filterStatus,  setFilterStatus]  = useState('All');

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/history`);
      setServerHistory(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchHistory();
    const iv = setInterval(fetchHistory, 3000);
    return () => clearInterval(iv);
  }, [fetchHistory]);

  const merged = (() => {
    const byId = {};
    serverHistory.forEach(r => { byId[r.run_id] = r; });
    jobList.forEach(j => { byId[j.run_id] = { ...byId[j.run_id], ...j }; });
    return Object.values(byId).sort((a,b) =>
      new Date(b.started_at) - new Date(a.started_at)
    );
  })();

  const reportTypes = ['All', '2b', '2a'];
  const filtered = merged.filter(r =>
    (filterClient === 'All' || r.report_type === filterClient) &&
    (filterStatus === 'All' || r.status === filterStatus)
  );

  const totalRuns    = merged.length;
  const completed    = merged.filter(r => r.status === 'completed').length;
  const totalRecords = merged.reduce((s,r) => s + (r.original_count||0), 0);
  const totalFlagged = merged.reduce((s,r) => s + (r.flagged_count||0), 0);

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', marginBottom:22, gap:12 }}>
        <div>
          <h2 style={{ fontFamily:'Syne,sans-serif', fontSize:22, fontWeight:600,
                       color: C.text, marginBottom:4 }}>
            Run History
          </h2>
          <p style={{ color: C.muted, fontSize:13 }}>
            All reconciliation runs — updates live every 3 seconds
          </p>
        </div>
        <button onClick={fetchHistory} style={{
          marginLeft:'auto', background: C.surface, border:`1px solid ${C.border2}`,
          borderRadius: radius.sm, padding:'7px 14px', color: C.textSoft,
          fontSize:12, cursor:'pointer', fontFamily:'Inter,sans-serif', fontWeight:500,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = C.accentBg; e.currentTarget.style.color = C.accent2; e.currentTarget.style.borderColor = C.accent + '44'; }}
        onMouseLeave={e => { e.currentTarget.style.background = C.surface; e.currentTarget.style.color = C.textSoft; e.currentTarget.style.borderColor = C.border2; }}
        >↻ Refresh</button>
      </div>

      {/* summary stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:22 }}>
        {[
          ['Total runs',    totalRuns,    C.accent,
            <svg key="s1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-5"/>
            </svg>],
          ['Completed',     completed,    C.success,
            <svg key="s2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/><polyline points="9 12 11 14 15 10"/>
            </svg>],
          ['Total records', totalRecords.toLocaleString('en-IN'), C.text,
            <svg key="s3" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.textSoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="4" x2="9" y2="20"/>
            </svg>],
          ['Total flagged', totalFlagged.toLocaleString('en-IN'),
             totalFlagged > 0 ? C.danger : C.success,
            <svg key="s4" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={totalFlagged > 0 ? C.danger : C.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 21V5a2 2 0 012-2h11l-2 4 2 4H6"/><line x1="4" y1="21" x2="4" y2="13"/>
            </svg>],
        ].map(([lbl, val, color, icon]) => (
          <div key={lbl} style={{ background: C.surface, border:`1px solid ${C.border2}`,
                                   borderRadius: radius.md, padding:'14px 18px',
                                   boxShadow: shadow.sm, position:'relative', overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <div style={{ fontSize:11, color: C.muted,
                            textTransform:'uppercase', letterSpacing:'.06em', fontWeight:600 }}>{lbl}</div>
              <div style={{ opacity:.7 }}>{icon}</div>
            </div>
            <div style={{ fontFamily:'DM Mono,monospace', fontSize:24,
                          fontWeight:600, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* filters */}
      <div style={{ display:'flex', gap:14, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        {[
          { label:'Report Type', options: reportTypes, val: filterClient, set: setFilterClient },
          { label:'Status', options:['All','processing','completed','failed'], val: filterStatus, set: setFilterStatus },
        ].map(({ label, options, val, set }) => (
          <div key={label} style={{ display:'flex', alignItems:'center', gap:7 }}>
            <span style={{ fontSize:12, color: C.muted, fontWeight:500 }}>{label}:</span>
            <select value={val} onChange={e => set(e.target.value)} style={{
              background: C.surface, border:`1px solid ${C.border2}`,
              borderRadius: radius.sm, padding:'6px 11px', color: C.text,
              fontSize:12, cursor:'pointer', outline:'none', fontWeight:500,
            }}>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
        <span style={{ fontSize:12, color: C.muted, marginLeft:'auto' }}>
          {filtered.length} runs
        </span>
      </div>

      {/* table */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color: C.muted }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, textAlign:'center', padding:40, color: C.muted }}>
          No runs match the selected filters
        </div>
      ) : (
        <div style={{ background: C.surface, border:`1px solid ${C.border2}`,
                      borderRadius: radius.lg, overflow:'hidden', boxShadow: shadow.sm }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background: C.surface2 }}>
                {['Run ID','Type','File','User','Started','Records','Flagged',
                  'Duration','Status','Actions'].map(h => (
                  <th key={h} style={{
                    padding:'11px 14px', textAlign:'left', fontSize:11,
                    color: C.muted, fontWeight:700, borderBottom:`1px solid ${C.border2}`,
                    whiteSpace:'nowrap', textTransform:'uppercase', letterSpacing:'.05em',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((run) => (
                <tr key={run.run_id} style={{
                  borderBottom:`1px solid ${C.border}`,
                  transition:'background .1s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = C.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding:'11px 14px', fontFamily:'DM Mono,monospace',
                               fontSize:11, color: C.muted }}>
                    {run.run_id.slice(-10)}
                  </td>
                  <td style={{ padding:'11px 14px' }}>
                    {run.report_type
                      ? <span style={{
                          fontSize:10, fontWeight:700, letterSpacing:'.05em',
                          color: run.report_type === '2b' ? C.accent2 : C.success,
                          background: run.report_type === '2b' ? C.accentBg : C.successBg,
                          border:`1px solid ${run.report_type === '2b' ? C.accent + '33' : C.success + '33'}`,
                          padding:'2px 8px', borderRadius:20,
                        }}>{run.report_type.toUpperCase()}</span>
                      : <span style={{ fontSize:12, color: C.muted }}>{run.client || 'default'}</span>}
                  </td>
                  <td style={{ padding:'11px 14px', fontSize:12, color: C.textSoft,
                               maxWidth:160, overflow:'hidden', textOverflow:'ellipsis',
                               whiteSpace:'nowrap' }}>
                    {run.filename || '—'}
                  </td>
                  <td style={{ padding:'11px 14px', fontSize:12, color: C.muted }}>
                    {run.username || '—'}
                  </td>
                  <td style={{ padding:'11px 14px', fontFamily:'DM Mono,monospace',
                               fontSize:11, color: C.muted, whiteSpace:'nowrap' }}>
                    {run.started_at?.slice(0,16).replace('T',' ')}
                  </td>
                  <td style={{ padding:'11px 14px', fontFamily:'DM Mono,monospace',
                               fontSize:13, color: C.text, fontWeight:500 }}>
                    {(run.original_count||0).toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding:'11px 14px', fontFamily:'DM Mono,monospace',
                               fontSize:13, fontWeight:500,
                               color: (run.flagged_count||0)>0 ? C.danger : C.success }}>
                    {(run.flagged_count||0).toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding:'11px 14px', fontFamily:'DM Mono,monospace',
                               fontSize:12, color: C.muted }}>
                    {run.duration ? `${run.duration}s` : run.status==='processing'
                      ? <span style={{ color: C.accent, animation:'pulse 1.2s infinite',
                                       display:'inline-block' }}>…</span>
                      : '—'}
                  </td>
                  <td style={{ padding:'11px 14px' }}>
                    <StatusBadge status={run.status} />
                  </td>
                  <td style={{ padding:'11px 14px' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => setSelected(run)} style={{
                        background: C.surface2, border:`1px solid ${C.border2}`,
                        borderRadius:5, padding:'5px 10px', color: C.textSoft,
                        fontSize:11, fontWeight:500, cursor:'pointer', whiteSpace:'nowrap',
                      }}>Details</button>
                      {run.output_file && (
                        <a href={`${API}/download/${run.output_file}`} download
                           style={{ textDecoration:'none' }}>
                          <button style={{
                            background: C.accentBg, border:`1px solid ${C.accent}44`,
                            borderRadius:5, padding:'5px 10px', color: C.accent2,
                            fontSize:11, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
                          }}>⬇ Download</button>
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DetailModal run={selected} onClose={() => setSelected(null)} />
    </div>
  );
}